import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { BookOpen, FlaskConical, Shield, Clock, AlertTriangle, Activity, ArrowLeft, ExternalLink, Printer } from 'lucide-react';
import axios from 'axios';

const API = `${""}/api`;

const ProtocolSeoPage = () => {
  const { slug } = useParams();
  const [protocol, setProtocol] = useState(null);
  const [seo, setSeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/protocol-guides/${slug}`)
      .then(r => {
        if (r.data?.success) {
          setProtocol(r.data.protocol);
          setSeo(r.data.seo);
          if (r.data.seo?.title) document.title = r.data.seo.title;
        } else {
          setError('Protocol not found');
        }
      })
      .catch(() => setError('Protocol not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-pulse text-slate-400">Loading protocol...</div>
    </div>
  );

  if (error || !protocol) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
      <h1 className="text-2xl font-bold text-slate-700">Protocol Not Found</h1>
      <Link to="/"><Button variant="outline">Back to Home</Button></Link>
    </div>
  );

  const refs = protocol.scientific_references || [];
  const indications = protocol.clinical_indications || [];
  const dosing = protocol.dosing_schedule || {};
  const safety = protocol.safety_profile || {};
  const monitoring = protocol.monitoring || {};
  const contraindications = protocol.contraindications || {};
  const timelines = protocol.expected_timelines || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* JSON-LD Schema */}
      {seo && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalGuideline",
          "name": protocol.name,
          "description": protocol.description,
          "url": seo.canonical_url,
          "guidelineSubject": { "@type": "MedicalCondition", "name": indications[0] || "Peptide Therapy" },
          "evidenceLevel": "https://schema.org/EvidenceLevelA",
          "publisher": { "@type": "Organization", "name": "PeptideProtocols.ai", "url": "https://peptideprotocols.ai" },
        })}} />
      )}

      {/* Header */}
      <header className="bg-white border-b border-slate-200 protocol-no-print">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-lg font-bold text-teal-700 no-underline" data-testid="seo-home-link">PeptideProtocols.ai</Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="seo-print-btn">
              <Printer className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Print</span>
            </Button>
            <Link to="/login">
              <Button variant="outline" size="sm" data-testid="seo-login-btn">Practitioner Login</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 py-3 protocol-no-print">
        <nav className="text-xs text-slate-500 flex items-center gap-1.5">
          <Link to="/" className="text-teal-600 hover:underline">Home</Link>
          <span>/</span>
          <Link to="/peptide-protocols" className="text-teal-600 hover:underline">Protocols</Link>
          <span>/</span>
          <span className="text-slate-700 font-medium">{protocol.name}</span>
        </nav>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 pb-16 protocol-print-area" data-testid="protocol-seo-page">
        <div className="max-w-4xl mx-auto space-y-8">

          {/* Title & Category */}
          <div>
            <Badge className="mb-2 bg-teal-50 text-teal-700 border-teal-200">{protocol.category}</Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-3" data-testid="protocol-name">{protocol.name}</h1>
            <p className="text-base text-slate-600 leading-relaxed">{protocol.description}</p>
          </div>

          {/* Mechanism of Action */}
          {protocol.mechanism_of_action && (
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mb-3">
                  <FlaskConical className="h-5 w-5 text-teal-600" /> Mechanism of Action
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">{protocol.mechanism_of_action}</p>
              </CardContent>
            </Card>
          )}

          {/* Clinical Indications */}
          {indications.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mb-3">
                  <Activity className="h-5 w-5 text-teal-600" /> Clinical Indications
                </h2>
                <ul className="grid md:grid-cols-2 gap-2">
                  {indications.map((ind, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-teal-500 mt-0.5">-</span> {ind}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Dosing */}
          {Object.keys(dosing).length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mb-3">
                  <Clock className="h-5 w-5 text-teal-600" /> Dosing Protocol
                </h2>
                <div className="space-y-3">
                  {Object.entries(dosing).map(([key, value]) => (
                    <div key={key} className="bg-slate-50 rounded-lg p-3">
                      <span className="text-xs font-semibold text-teal-700 uppercase">{key.replace(/_/g, ' ')}</span>
                      <p className="text-sm text-slate-600 mt-1">{value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Safety & Contraindications */}
          <div className="grid md:grid-cols-2 gap-6">
            {safety.common_side_effects && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mb-3">
                    <Shield className="h-5 w-5 text-amber-600" /> Safety Profile
                  </h2>
                  <div className="space-y-2">
                    {(Array.isArray(safety.common_side_effects) ? safety.common_side_effects : []).map((se, i) => (
                      <div key={i} className="text-sm text-slate-600">
                        <span className="font-medium">{typeof se === 'string' ? se : se.effect}</span>
                        {se.frequency && <span className="text-slate-400 ml-1">({se.frequency})</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {contraindications.absolute_contraindications && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-red-500" /> Contraindications
                  </h2>
                  <ul className="space-y-1.5">
                    {contraindications.absolute_contraindications.map((c, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="text-red-400 mt-0.5">-</span> {c}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Scientific References */}
          {refs.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mb-3">
                  <BookOpen className="h-5 w-5 text-teal-600" /> Scientific References
                </h2>
                <div className="space-y-3">
                  {refs.map((ref, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-slate-800">{ref.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{ref.authors} — {ref.journal}, {ref.year}</p>
                      {ref.key_finding && <p className="text-xs text-teal-700 mt-1 italic">{ref.key_finding}</p>}
                      {ref.pubmed_id && (
                        <a href={`https://pubmed.ncbi.nlm.nih.gov/${ref.pubmed_id}/`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline mt-1">
                          PMID: {ref.pubmed_id} <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* CTA */}
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-8 text-center protocol-no-print">
            <h2 className="text-xl font-bold text-white mb-2">Generate Personalized {protocol.name} Protocols</h2>
            <p className="text-teal-100 text-sm mb-4 max-w-md mx-auto">
              Dr. Peptide creates individualized {protocol.name} protocols based on your patient's labs, genetics, and health data.
            </p>
            <Link to="/signup">
              <Button className="bg-white text-teal-700 hover:bg-teal-50 px-6 py-3" data-testid="seo-cta-signup">
                Get Started — Pre-Release Access
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-6 protocol-no-print">
        <div className="container mx-auto px-4 text-center text-xs text-slate-400">
          © June 4, 2025 Dallas Regenerative LLC |{' '}
          <Link to="/terms" className="text-teal-600 hover:underline">Terms</Link> |{' '}
          <Link to="/privacy" className="text-teal-600 hover:underline">Privacy</Link>
        </div>
      </footer>
    </div>
  );
};

export default ProtocolSeoPage;
